import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CheckSquare, Lock, Paperclip, Plus, Search } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { fmtDate } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusChip, Tag, tagTone } from "@/components/common/StatusChip";
import { AgeBadge, UserAvatar } from "@/components/common/Bits";
import BulkCloseBar from "./BulkCloseBar";
import NewTicketDialog from "./NewTicketDialog";

const STATUS_OPTIONS = [["active", "Active"], ["open", "Open"], ["working", "Working"], ["pending_approval", "Awaiting approval"], ["closed", "Closed (archive)"], ["all", "Everything"]];
const closable = (t) => t.status !== "closed" && t.status !== "pending_approval";

export default function TicketList({ tickets, total, loading, filters, setFilters, selectedId, onSelect, className }) {
  const { can, readOnly } = useAuth();
  const [newOpen, setNewOpen] = useState(false);
  const [selecting, setSelecting] = useState(false);
  const [picked, setPicked] = useState(() => new Set());
  const { data: channels = [] } = useQuery({ queryKey: ["channels"], queryFn: () => api.get("/channels").then((r) => r.data) });
  const { data: users = [] } = useQuery({ queryKey: ["users"], queryFn: () => api.get("/users").then((r) => r.data) });
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: () => api.get("/settings").then((r) => r.data) });
  const set = (k) => (v) => setFilters((f) => ({ ...f, [k]: v }));
  const canBulk = can("tickets.close") && !readOnly;
  const toggle = (id) => setPicked((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const stopSelecting = () => { setSelecting(false); setPicked(new Set()); };

  return (
    <aside className={cn("w-full shrink-0 flex-col border-r border-border bg-card lg:w-[420px]", className)} data-testid="ticket-list-pane">
      <div className="space-y-2 border-b border-border p-3">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input value={filters.q} onChange={(e) => set("q")(e.target.value)} placeholder="Search subject, sender, TT-0001…" className="h-8 pl-8 text-xs" data-testid="ticket-search-input" />
          </div>
          {canBulk && (
            <Button size="sm" variant={selecting ? "secondary" : "outline"} className="h-8" onClick={() => (selecting ? stopSelecting() : setSelecting(true))} title="Select several tickets to close at once" data-testid="select-tickets-btn"><CheckSquare className="h-3.5 w-3.5" /></Button>
          )}
          {can("tickets.create") && !readOnly && (
            <Button size="sm" className="h-8" onClick={() => setNewOpen(true)} data-testid="new-ticket-btn"><Plus className="mr-1 h-3.5 w-3.5" /> New</Button>
          )}
        </div>
        <div className="grid grid-cols-3 gap-2">
          <Select value={filters.status} onValueChange={set("status")}>
            <SelectTrigger className="h-8 text-xs" data-testid="filter-status-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-popover">{STATUS_OPTIONS.map(([v, l]) => <SelectItem key={v} value={v} className="text-xs">{l}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.channel_id} onValueChange={set("channel_id")}>
            <SelectTrigger className="h-8 text-xs" data-testid="filter-channel-select"><SelectValue placeholder="Channel" /></SelectTrigger>
            <SelectContent className="bg-popover">
              <SelectItem value="all" className="text-xs">All channels</SelectItem>
              {channels.map((c) => <SelectItem key={c.id} value={c.id} className="text-xs">{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.assignee} onValueChange={set("assignee")}>
            <SelectTrigger className="h-8 text-xs" data-testid="filter-assignee-select"><SelectValue placeholder="Assignee" /></SelectTrigger>
            <SelectContent className="bg-popover">
              <SelectItem value="all" className="text-xs">Anyone</SelectItem>
              <SelectItem value="me" className="text-xs">Mine</SelectItem>
              <SelectItem value="unassigned" className="text-xs">Unassigned</SelectItem>
              {users.filter((u) => u.active).map((u) => <SelectItem key={u.id} value={u.id} className="text-xs">{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center justify-between">
          <span className="eyebrow" data-testid="ticket-count">{loading ? "loading…" : `${total} ticket${total === 1 ? "" : "s"}`}</span>
          {filters.tag && <button className="eyebrow text-amber-500 hover:underline" onClick={() => set("tag")("")}>tag: {filters.tag} ×</button>}
        </div>
      </div>
      {selecting && <BulkCloseBar selected={picked} selectable={tickets.filter(closable).length} onSelectAll={() => setPicked(new Set(tickets.filter(closable).map((t) => t.id)))} onClear={stopSelecting} onDone={stopSelecting} />}
      <ul className="min-h-0 flex-1 divide-y divide-border overflow-auto" data-testid="ticket-list">
        {!loading && tickets.length === 0 && <li className="p-8 text-center text-xs text-muted-foreground" data-testid="ticket-list-empty">No tickets match these filters.</li>}
        {tickets.map((t) => {
          const assignee = users.find((u) => u.id === t.assignee_id);
          const isPicked = picked.has(t.id);
          return (
            <li key={t.id} className={cn(selecting && "flex items-stretch", isPicked && "bg-primary/5")}>
              {selecting && (
                <label className={cn("flex shrink-0 cursor-pointer items-center px-3", !closable(t) && "cursor-not-allowed opacity-40")} title={closable(t) ? "Select" : "Already closed or awaiting approval"}>
                  <Checkbox checked={isPicked} disabled={!closable(t)} onCheckedChange={() => toggle(t.id)} data-testid={`ticket-select-${t.number}`} />
                </label>
              )}
              <button onClick={() => (selecting ? closable(t) && toggle(t.id) : onSelect(t))} data-testid={`ticket-row-${t.number}`}
                className={cn("row-hover relative w-full min-w-0 px-3 py-2.5 text-left", selectedId === t.id && "bg-accent", selecting && "pl-0")}>
                {selectedId === t.id && <span className="absolute inset-y-0 left-0 w-0.5 bg-primary" />}
                <div className="flex items-center gap-2">
                  <span className="mono text-[11px] font-semibold text-muted-foreground">{t.number}</span>
                  <span className="flex items-center gap-1 text-[11px] text-muted-foreground"><span className="h-2 w-2" style={{ background: t.channel_color }} />{t.channel_name}</span>
                  <span className="ml-auto flex items-center gap-2">
                    {t.attachment_count > 0 && <span className="flex items-center gap-0.5 text-[11px] text-muted-foreground"><Paperclip className="h-3 w-3" />{t.attachment_count}</span>}
                    <AgeBadge seconds={t.status === "closed" ? t.resolution_seconds : t.age_seconds} warnHours={settings?.rotting_warn_hours} criticalHours={settings?.rotting_critical_hours} />
                  </span>
                </div>
                <div className="mt-1 truncate text-sm font-semibold">{t.subject}</div>
                <div className="mt-0.5 truncate text-xs text-muted-foreground">
                  {t.from_name || t.from_email}
                  {t.locked ? <span className="ml-1 inline-flex items-center gap-1 italic" data-testid={`ticket-locked-${t.number}`}><Lock className="h-3 w-3" /> {t.first_worked_at ? "paused — content hidden" : "content hidden until worked"}</span> : ` — ${t.preview}`}
                </div>
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                  <StatusChip status={t.status} />
                  {t.tags.map((tag) => <Tag key={tag} tone={tagTone(tag)}>{tag}</Tag>)}
                  <span className="ml-auto flex items-center gap-1.5 text-[11px] text-muted-foreground">
                    {assignee ? <><UserAvatar name={assignee.name} src={assignee.avatar_url} size="xs" />{assignee.name}</> : <span className="italic">Unassigned</span>}
                    <span className="mono">· {fmtDate(t.created_at)}</span>
                  </span>
                </div>
              </button>
            </li>
          );
        })}
      </ul>
      <NewTicketDialog open={newOpen} onOpenChange={setNewOpen} channels={channels} />
    </aside>
  );
}
